import type { User } from "oidc-client-ts";

/**
 * ⚠️ LES DETTE FØR DU BRUKER ROLLENE HERFRA TIL NOE ANNET ENN Å VISE FLISER.
 *
 * Launcheren er ingen sikkerhetsgrense. Den avgjør kun hvilke fliser som
 * *vises*, og det er ren brukeropplevelse - ikke autorisasjon. En bruker som
 * redigerer sitt eget token i nettleseren får en flis til, og nøyaktig
 * ingenting mer: klikker hen på den, møter hen Reports/Companion/Cabinet, som
 * gjør den ekte kontrollen server-side.
 *
 * Den ekte kontrollen, som HVER app må gjøre på hvert eneste token:
 *   signatur mot realmens JWKS, `iss`, `aud` (= appens egen clientId),
 *   `exp`, og til slutt at realm-rollen finnes.
 * Se seksjon 4 i README-en til actiwise-idp.
 *
 * Å skjule en flis er altså kosmetikk. Å slippe noen inn er appens jobb.
 */

interface RealmAccess {
  roles?: string[];
}

interface AccessTokenPayload {
  realm_access?: RealmAccess;
}

/**
 * Dekoder payloaden i et JWT. Ingen signaturvalidering - den er allerede gjort
 * av oidc-client-ts mot realmens JWKS da tokenet ble hentet. Her leser vi bare
 * ut innholdet for å vite hvilke fliser vi skal tegne.
 */
function dekodPayload(token: string): unknown {
  const deler = token.split(".");
  const payload = deler[1];
  if (deler.length !== 3 || !payload) {
    throw new Error("Access-tokenet ser ikke ut som et JWT");
  }

  const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
  const binaer = atob(base64);

  // atob gir latin-1; claims kan inneholde æøå og andre flerbyte-tegn.
  const bytes = Uint8Array.from(binaer, (tegn) => tegn.charCodeAt(0));
  return JSON.parse(new TextDecoder().decode(bytes));
}

/**
 * Realm-rollene fra access-tokenet.
 *
 * De ligger i access-tokenet og ikke i ID-tokenet, fordi Keycloaks
 * `roles`-scope som standard har `access.token.claim: true` og
 * `id.token.claim: false`. Vil dere heller ha dem i ID-tokenet, er det en
 * mapper å legge til i realmen - men det er ikke nødvendig for dette.
 */
export function rollerFra(bruker: User): Set<string> {
  if (!bruker.access_token) return new Set();

  try {
    const payload = dekodPayload(bruker.access_token) as AccessTokenPayload;
    return new Set(payload.realm_access?.roles ?? []);
  } catch (feil) {
    // Klarer vi ikke lese rollene, viser vi ingen fliser i stedet for å gjette.
    // Brukeren ser da en tom portal med en forklaring, ikke en falsk tilgang.
    console.error("Kunne ikke lese roller fra access-tokenet:", feil);
    return new Set();
  }
}

export interface Profil {
  navn: string;
  epost: string | null;
}

export function profilFra(bruker: User): Profil {
  const p = bruker.profile;
  return {
    navn: p.name ?? p.preferred_username ?? p.email ?? "Ukjent bruker",
    epost: p.email ?? null,
  };
}
